import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  AccessToken_CreateAccessTokenResult,
  AccessToken_FieldsFragment,
  AccessToken_GetAccessTokensByPortalIdDocument,
  AccessTokenPermission,
  AccessTokenScope,
  useAccessToken_CreateAccessTokenMutation,
} from 'queries';
import * as yup from 'yup';

import FormSelectField from 'common/form-select-field';
import FormTextField from 'common/form-text-field';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useCurrentUser from 'utils/useCurrentUser';

const useStyles = makeStyles(() => ({
  content: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
  },
  title: {
    fontWeight: 700,
    fontSize: '24px',
    marginTop: '18px',
    alignSelf: 'center',
  },
  saveButton: {
    borderRadius: '100px',
    maxHeight: '40px',
    marginTop: '40px',
    marginBottom: '20px',
    backgroundColor: '#5ED1F0',
    color: '#1C2541',
  },
}));

type Props = {
  setCreatedAccessTokenResult: (
    value: Pick<AccessToken_CreateAccessTokenResult, 'privateAccessKeyText'> & {
      accessToken: AccessToken_FieldsFragment;
    },
  ) => void;
  setShowSuccessModal: (value: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onExitClick: Function;
};

type CreateAccessTokenFormValues = {
  name: string;
  permission: AccessTokenPermission;
  scope: AccessTokenScope;
  isUserSpecificToken: boolean;
};

const schema = yup.object().shape({
  name: yup
    .string()
    .required('Please enter a name')
    .matches(/^[a-zA-Z0-9]+$/, 'Must be only letters and numbers')
    .min(1, 'Minimum characters is 1')
    .max(50, 'Maximum characters is 50'),
  permission: yup.string().required('Please select a permission'),
  scope: yup.string().required('Please select a scope'),
  isUserSpecificToken: yup.boolean().required('Please specify if this is a user specific token'),
});

const permissionSelectOptions = [{ label: 'All', value: AccessTokenPermission.All }];

const scopeSelectOptions = [
  { label: 'All', value: AccessTokenScope.All },
  { label: 'Salesforce', value: AccessTokenScope.Salesforce },
  { label: 'Zapier', value: AccessTokenScope.Zapier },
];

export default function CreateAccessTokenForm({
  setCreatedAccessTokenResult,
  setShowSuccessModal,
  onExitClick,
}: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const currentUser = useCurrentUser();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateAccessTokenFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      permission: AccessTokenPermission.All,
      scope: AccessTokenScope.All,
      isUserSpecificToken: false,
    },
  });

  const [createAccessToken] = useAccessToken_CreateAccessTokenMutation({
    refetchQueries: [
      {
        query: AccessToken_GetAccessTokensByPortalIdDocument,
        variables: { portalId },
      },
    ],
    onCompleted: (data) => {
      showToast({
        message: 'The access token has been created.',
        severity: NotificationSeverity.SUCCESS,
      });

      setCreatedAccessTokenResult(data.data);
      setShowSuccessModal(true);

      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error creating access token: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (!currentUser) {
    return <Spinner />;
  }

  const onSubmit = async (values: CreateAccessTokenFormValues) => {
    const { name, permission, scope, isUserSpecificToken } = values;

    let userId: string | null = null;
    if (isUserSpecificToken) {
      userId = currentUser.id!;
    }

    await createAccessToken({
      variables: {
        input: {
          name,
          permission,
          scope,
          portalId,
          userId,
        },
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.content}>
        <Typography className={classes.title}>Create Access Token</Typography>
        <div>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <FormTextField
                {...field}
                inputRef={ref}
                label="Token Name"
                errorMessage={errors.name?.message}
              />
            )}
          />
        </div>
        <div>
          <Controller
            name="permission"
            control={control}
            render={({ field: { ref: _, ...field } }) => (
              <FormSelectField
                {...field}
                errorMessage={errors.permission?.message}
                options={permissionSelectOptions}
                label="Permission"
                placeholder="Select permission"
              />
            )}
          />
        </div>
        <div>
          <Controller
            name="scope"
            control={control}
            render={({ field: { ref: _, ...field } }) => (
              <FormSelectField
                {...field}
                errorMessage={errors.scope?.message}
                options={scopeSelectOptions}
                label="Scope"
                placeholder="Select scope"
              />
            )}
          />
        </div>
        <div>
          <Controller
            name="isUserSpecificToken"
            control={control}
            render={({ field: { ref, value, onChange, ...field } }) => (
              <FormSelectField
                {...field}
                value={value === true ? 'yes' : 'no'}
                onChange={(e) => onChange(e.target.value === 'yes')}
                inputRef={ref}
                label="Is token specific to your user account?"
                options={[
                  {
                    label: 'Yes',
                    value: 'yes',
                  },
                  {
                    label: 'No',
                    value: 'no',
                  },
                ]}
              />
            )}
          />
        </div>
        <Button
          type="submit"
          variant="outlined"
          className={classes.saveButton}
          disabled={isSubmitting}
        >
          Create
        </Button>
      </div>
    </form>
  );
}
